import React from 'react'
import { Platform, Share } from 'react-native'

import PdfDownloadLoader from '@zet/common/components/pdfDownloadLoader'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import VideoBanner from '@zet/common/components/videoBanner'
import useSharePdf from '@zet/common/hooks/useSharePdf'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, SH3 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import { i18n } from '@zet/localization'
import PDF from '@zet/svg/pdf'
import { isApp } from '@zet/utils'
import { navigationRef } from '@zet/utils/navigation'

const ApplicationProcessContent = ({
  brandId,
  isRecommendationFlow,
  isFixedHeight,
  setIsBottomSheetVisible,
}: {
  brandId: string | number
  isRecommendationFlow?: boolean
  isFixedHeight?: boolean
  setIsBottomSheetVisible?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { share, isLoading } = useSharePdf()
  const { data: content } = useBrandDetailsById(brandId)
  const onShare = async (videoId?: string) => {
    try {
      await Share.share({
        message: `https://www.youtube.com/watch?v=${videoId}`,
      })
    } catch (error) {
      console.warn(error)
    }
  }
  const ShareButton = ({
    isClassName,
    videoId,
  }: {
    isClassName?: boolean
    videoId?: string
  }) => {
    return (
      <PressableWithIntent
        name="ApplicationProcessShare"
        className={isClassName ? 'bg-grays-20 absolute rounded-lg p-2' : ''}
        onPress={() => {
          if (isClassName) {
            onShare(videoId)
          } else {
            share(content?.applicationProcess?.pdfLink)
          }
        }}
        eventName="click_action"
        eventPayload={{
          item_name: isRecommendationFlow
            ? 'application_process_recommendation_flow'
            : 'brands_list_application_process',
          share_type: isClassName ? 'video' : 'pdf',
        }}
      >
        <Icon type="Share" />
      </PressableWithIntent>
    )
  }
  if (isLoading) {
    return <PdfDownloadLoader />
  }
  return (
    <Stack
      className="flex-1 bg-white px-3 pt-3"
      style={isFixedHeight ? { marginBottom: 40 } : {}}
    >
      <SH3>
        {i18n.t('applicationProcess.heading', { bankName: content?.name })}
      </SH3>
      {APP_NAME === 'zet-app' && (
        <>
          <B2 className="my-3">
            {i18n.t('applicationProcess.description', {
              bankName: content?.name,
            })}
          </B2>

          <VideoBanner
            videoId={content?.applicationProcess?.videoLink}
            videoData={content?.applicationProcess?.videoContent}
            image={content?.applicationProcess?.thumbnail}
            eventName="click_action"
            eventPayload={{
              item_name: isRecommendationFlow
                ? 'application_process_video_recommendation_flow'
                : 'brands_list_application_process_video',
            }}
          />
          <Stack className="m-1 -mt-1 items-end justify-end">
            <ShareButton
              isClassName
              videoId={content?.applicationProcess?.videoLink}
            />
          </Stack>
        </>
      )}
      {content?.applicationProcess?.pdfLink ? (
        <XStack className="border-grays-40 bg-grays-20 mt-5 items-center justify-between rounded border pb-3 pl-3 pr-0 pt-2">
          <Pressable
            className="-ml-2"
            style={{ flex: 0.9 }}
            onPress={() => {
              setIsBottomSheetVisible && setIsBottomSheetVisible(false)
              if (Platform.OS === 'web') {
                window.open(content?.applicationProcess?.pdfLink, '_blank')
              } else {
                navigationRef.navigate('PdfScreen', {
                  pdfTitle: content?.name,
                  pdfUrl: content?.applicationProcess?.pdfLink,
                })
              }
            }}
            eventName="click_action"
            eventPayload={{
              item_name: isRecommendationFlow
                ? 'application_process_pdf_recommendation_flow'
                : 'brands_list_application_process_pdf',
            }}
          >
            <XStack className="items-center">
              <PDF />
              <Stack className="flex-1 pl-2">
                <SH3>{i18n.t('applicationProcess.process_booklet')}</SH3>
                <B2 className="text-grays-80">
                  {i18n.t('applicationProcess.process_step', {
                    bankName: content?.name,
                  })}
                </B2>
              </Stack>
            </XStack>
          </Pressable>
          {isApp && (
            <PressableWithIntent
              name="ApplicationProcessShare"
              onPress={() => share(content?.applicationProcess?.pdfLink)}
              style={{ flex: 0.1 }}
            >
              <Stack className="flex-1 items-center justify-center">
                <ShareButton />
              </Stack>
            </PressableWithIntent>
          )}
        </XStack>
      ) : (
        <></>
      )}
    </Stack>
  )
}

export default ApplicationProcessContent
